import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const CONFIRM_WORD = "DELETE";

const ConfirmDeleteDialog = ({
  open,
  loading,
  error,
  onConfirm,
  onCancel,

  title        = "Delete Allocation",
  subtitle     = "This permanently removes the request — it cannot be undone",
  confirmLabel = "Delete Permanently",
  warningText  = "This will permanently delete this allocation request and all related " +
                 "notifications and approval history. This cannot be undone.",

  primaryText,
  badgeText,
  subText,
}) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const [typed, setTyped] = useState("");

  // Reset the typed word whenever the dialog closes/reopens for a new row,
  // so a previous confirmation can't linger and accidentally arm the button.
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  if (!open) return null;

  const isArmed = typed === CONFIRM_WORD;

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={loading ? undefined : onCancel}
        sx={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 1300,
          cursor: loading ? "not-allowed" : "default",
        }}
      />

      {/* Dialog */}
      <Box sx={{
        position: "fixed",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1301,
        width: "400px",
        background: t.surface,
        border: `1px solid ${t.border2}`,
        borderLeft: "3px solid #c84040",
        borderRadius: "6px",
        overflow: "hidden",
        boxShadow: theme.palette.mode === "dark"
          ? "0 8px 32px rgba(0,0,0,0.5)"
          : "0 8px 32px rgba(0,0,0,0.15)",
      }}>

        {/* Header */}
        <Box sx={{ padding: "16px 20px 12px", borderBottom: `1px solid ${t.border}` }}>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "14px", fontWeight: 700, color: t.text, mb: "2px" }}>
            {title}
          </Typography>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, letterSpacing: "0.04em" }}>
            {subtitle}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ padding: "16px 20px" }}>
          <Box sx={{
            padding: "10px 14px", background: t.surface2,
            border: `1px solid ${t.border2}`, borderRadius: "4px", mb: "14px",
          }}>
            {primaryText && (
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600, color: t.text, mb: "2px" }}>
                {primaryText}
              </Typography>
            )}
            {badgeText && (
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.accent, mb: "1px" }}>
                {badgeText}
              </Typography>
            )}
            {subText && (
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: t.text3 }}>
                {subText}
              </Typography>
            )}
          </Box>

          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text2, lineHeight: 1.6, mb: "14px" }}>
            {warningText}
          </Typography>

          {/* Typed confirmation gate */}
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3, mb: "6px" }}>
            Type <Box component="span" sx={{ color: "#c84040", fontWeight: 700 }}>{CONFIRM_WORD}</Box> to confirm
          </Typography>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={loading}
            placeholder={CONFIRM_WORD}
            autoFocus
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "8px 10px",
              borderRadius: "3px",
              border: `1px solid ${isArmed ? "#c84040" : t.border2}`,
              background: t.surface2,
              color: t.text,
              fontFamily: "'DM Mono', monospace",
              fontSize: "12px",
              outline: "none",
            }}
          />
        </Box>

        {/* Error */}
        {error && (
          <Box sx={{ padding: "0 20px 12px" }}>
            <Box sx={{ padding: "8px 12px", background: "rgba(200,64,64,0.08)", border: "1px solid rgba(200,64,64,0.25)", borderRadius: "3px" }}>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#c84040", letterSpacing: "0.04em" }}>
                {error}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", padding: "12px 20px", borderTop: `1px solid ${t.border}` }}>
          <Box
            onClick={loading ? undefined : onCancel}
            sx={{
              padding: "7px 18px", borderRadius: "3px",
              cursor: loading ? "not-allowed" : "pointer",
              border: `1px solid ${t.border2}`,
              fontFamily: "'DM Mono', monospace", fontSize: "11px",
              color: loading ? t.text3 : t.text2,
              transition: "all 0.15s",
              "&:hover": loading ? {} : { borderColor: t.border3, color: t.text },
            }}
          >
            Cancel
          </Box>

          <Box
            onClick={(loading || !isArmed) ? undefined : onConfirm}
            sx={{
              padding: "7px 18px", borderRadius: "3px",
              cursor: (loading || !isArmed) ? "not-allowed" : "pointer",
              background: isArmed ? "rgba(200,64,64,0.12)" : "rgba(120,120,120,0.08)",
              border: `1px solid ${isArmed ? "rgba(200,64,64,0.35)" : t.border2}`,
              fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 600,
              color: loading ? "rgba(200,64,64,0.4)" : isArmed ? "#c84040" : t.text3,
              display: "flex", alignItems: "center", gap: "6px",
              transition: "all 0.15s",
              "&:hover": (loading || !isArmed) ? {} : { background: "rgba(200,64,64,0.2)" },
            }}
          >
            {loading && (
              <Box sx={{
                width: "10px", height: "10px", borderRadius: "50%",
                border: "1.5px solid rgba(200,64,64,0.3)",
                borderTopColor: "#c84040",
                animation: "spin 0.7s linear infinite",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
              }} />
            )}
            {loading ? "Deleting..." : confirmLabel}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ConfirmDeleteDialog;
